import { useMemo } from 'react';
import type { BlogPost } from '@/hooks/useBlogPosts';

interface BrowseByTopicProps {
  posts: BlogPost[];
  activeCategory: string;
  onCategoryChange: (category: string) => void;
}

export default function BrowseByTopic({
  posts,
  activeCategory,
  onCategoryChange,
}: BrowseByTopicProps) {
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';

  // Categories with counts, most-used first then alphabetical.
  const categories = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    posts.forEach((post) => {
      const cat = post.category?.trim();
      if (!cat) return;
      const key = cat.toLowerCase();
      const existing = counts.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(key, { label: cat, count: 1 });
      }
    });
    return Array.from(counts.values()).sort(
      (a, b) => b.count - a.count || a.label.localeCompare(b.label),
    );
  }, [posts]);

  const topics = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    posts.forEach((post) => {
      (post.tags || []).forEach((tag) => {
        const trimmed = tag.trim();
        if (!trimmed) return;
        const key = trimmed.toLowerCase();
        const existing = counts.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          counts.set(key, { label: trimmed, count: 1 });
        }
      });
    });
    return Array.from(counts.values()).sort(
      (a, b) => b.count - a.count || a.label.localeCompare(b.label),
    );
  }, [posts]);

  if (categories.length === 0 && topics.length === 0) return null;

  const chipBase =
    'inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap';

  return (
    <section className="max-w-6xl mx-auto px-6 pt-10 md:pt-12">
      <div className="bg-background-100/70 border border-background-200/70 rounded-lg p-6 md:p-8 space-y-6">
        {/* Category filter */}
        {categories.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-filter-3-line text-secondary-700" />
              </span>
              <div>
                <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
                  Filter by Category
                </h2>
                <p className="text-xs text-foreground-500">
                  Narrow the articles below to a theme
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => onCategoryChange('All')}
                className={`${chipBase} ${
                  activeCategory === 'All'
                    ? 'bg-primary-500 text-background-50 border border-primary-500'
                    : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                All
                <span
                  className={`text-[10px] font-semibold ${
                    activeCategory === 'All' ? 'text-background-50/80' : 'text-foreground-400'
                  }`}
                >
                  {posts.length}
                </span>
              </button>
              {categories.map((cat) => {
                const isActive = activeCategory.toLowerCase() === cat.label.toLowerCase();
                return (
                  <button
                    key={cat.label}
                    type="button"
                    onClick={() => onCategoryChange(cat.label)}
                    className={`${chipBase} ${
                      isActive
                        ? 'bg-primary-500 text-background-50 border border-primary-500'
                        : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300 hover:text-primary-700'
                    }`}
                  >
                    {cat.label}
                    <span
                      className={`text-[10px] font-semibold ${
                        isActive ? 'text-background-50/80' : 'text-foreground-400'
                      }`}
                    >
                      {cat.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Topic cloud */}
        {topics.length > 0 && (
          <div className="pt-6 border-t border-background-200/70">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-price-tag-3-line text-primary-700" />
              </span>
              <div>
                <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
                  Browse by Topic
                </h2>
                <p className="text-xs text-foreground-500">
                  Jump straight into what matters to you
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {topics.map((topic) => (
                <a
                  key={topic.label}
                  href={`${pathPrefix}/blog/tag/${encodeURIComponent(topic.label)}`}
                  className="group inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-background-50 text-foreground-600 border border-background-200/70 transition-colors hover:bg-secondary-500 hover:text-background-50 hover:border-secondary-500 cursor-pointer whitespace-nowrap"
                >
                  #{topic.label}
                  <span className="text-[10px] font-semibold text-foreground-400 group-hover:text-background-50/80 transition-colors">
                    {topic.count}
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}